import type { ReactElement, ReactNode } from 'react';
import { useMemo } from 'react';
import type { GridColDef, GridRenderCellParams, GridValidRowModel } from '@mui/x-data-grid-pro';

import { useDataGridMasterDetailColumns } from '../../DataGridMasterDetail/src/DataGridMasterDetail.context';
import { DataTableGrid, DataTableRoot } from './DataTable.styled';

export const DATA_TABLE_TOTAL_ROW_FLAG = 'isTotalRow' as const;
export const DATA_TABLE_CELL_ELLIPSIS_CLASS = 'data-table-cell-ellipsis';

export type DataTableColumn<R extends GridValidRowModel> = Omit<GridColDef<R>, 'editable'> & {
  /** When true, column cells are read-only unless `isCellEditable` overrides. */
  disabled?: boolean;
  /** When false, column is hidden. Also hidden if parent master field is hidden. */
  visible?: boolean;
};

export type DataTableSummaryCell = {
  field: string;
  value?: ReactNode;
  /** When false, summary cell renders empty. Defaults to true. */
  visible?: boolean;
};

export type DataTableSummary = {
  /** When false, summary row is hidden. Defaults to true. */
  visible?: boolean;
  id?: string | number;
  /** Label shown in `labelField` (e.g. "Show Total"). */
  label?: string;
  /** Field that receives `label`. */
  labelField?: string;
  cells: DataTableSummaryCell[];
};

export type DataTableRow = GridValidRowModel & {
  id: string | number;
  isTotalRow?: boolean;
};

export type DataTableProps<R extends DataTableRow> = {
  /** When false, table is not rendered. Defaults to true. */
  visible?: boolean;
  rows: R[];
  columns: DataTableColumn<R>[];
  summary?: DataTableSummary;
  /**
   * Per-cell edit override. Returning true makes the cell editable even when
   * the column has `disabled: true`.
   */
  isCellEditable?: (row: R, field: string) => boolean;
  processRowUpdate?: (newRow: R, oldRow: R) => R | Promise<R>;
  getRowId?: (row: R) => string | number;
  columnHeaderHeight?: number;
  rowHeight?: number;
  dataTestID?: string;
};

function buildSummaryRow<R extends DataTableRow>(summary: DataTableSummary): R {
  const row: Record<string, unknown> = {
    id: summary.id ?? 'data-table-summary',
    [DATA_TABLE_TOTAL_ROW_FLAG]: true,
  };

  if (summary.labelField) {
    row[summary.labelField] = summary.label ?? '';
  }

  for (const cell of summary.cells) {
    row[cell.field] = cell.visible === false ? '' : (cell.value ?? '');
  }

  return row as R;
}

function renderEllipsisCell(content: ReactNode, title?: string): ReactNode {
  if (content == null || content === '') {
    return content;
  }

  return (
    <span className={DATA_TABLE_CELL_ELLIPSIS_CLASS} title={title}>
      {content}
    </span>
  );
}

/**
 * Shared data table built on MUI X DataGridPro.
 * @see https://mui.com/material-ui/react-table/#data-table
 */
export function DataTable<R extends DataTableRow>({
  visible = true,
  rows,
  columns,
  summary,
  isCellEditable,
  processRowUpdate,
  getRowId = (row) => row.id,
  columnHeaderHeight = 48,
  rowHeight = 32,
  dataTestID = 'data-table',
}: Readonly<DataTableProps<R>>): ReactElement | null {
  const { hiddenFields: parentHiddenFields } = useDataGridMasterDetailColumns();
  const showSummary = summary?.visible !== false && Boolean(summary);

  const gridColumns = useMemo(() => {
    return columns
      .filter((column) => {
        if (column.visible === false) {
          return false;
        }
        if (parentHiddenFields.has(column.field)) {
          return false;
        }
        return true;
      })
      .map((column) => {
        const { disabled = false, visible: _visible, renderCell, ...rest } = column;
        return {
          ...rest,
          editable: !disabled || Boolean(isCellEditable),
          renderCell: (params: GridRenderCellParams<R>) => {
            const displayValue = params.formattedValue ?? params.value;
            const content = renderCell ? renderCell(params) : displayValue;
            const title = displayValue == null || displayValue === '' ? undefined : String(displayValue);

            return renderEllipsisCell(content, title);
          },
        } satisfies GridColDef<R>;
      });
  }, [columns, isCellEditable, parentHiddenFields]);

  const pinnedRows = useMemo(() => {
    if (!showSummary || !summary) {
      return undefined;
    }

    const filteredSummary = {
      ...summary,
      cells: summary.cells.filter((cell) => cell.visible !== false && !parentHiddenFields.has(cell.field)),
    };

    return { bottom: [buildSummaryRow<R>(filteredSummary)] };
  }, [showSummary, summary, parentHiddenFields]);

  const pinnedBottomRowCount = showSummary ? 1 : 0;
  const gridHeight = columnHeaderHeight + (rows.length + pinnedBottomRowCount) * rowHeight;

  if (!visible) {
    return null;
  }

  return (
    <DataTableRoot data-testid={dataTestID}>
      <DataTableGrid
        rows={rows}
        columns={gridColumns}
        pinnedRows={pinnedRows}
        getRowId={(row) => getRowId(row as R)}
        getRowClassName={(params) => ((params.row as R).isTotalRow ? 'data-table-total-row' : '')}
        getCellClassName={(params) => {
          const row = params.row as R;
          if (row.isTotalRow) {
            return '';
          }
          return isCellEditable?.(row, params.field) ? 'data-table-editable-cell' : '';
        }}
        isCellEditable={(params) => {
          const row = params.row as R;
          if (row.isTotalRow) {
            return false;
          }

          const column = columns.find((item) => item.field === params.field);
          const cellOverride = isCellEditable?.(row, params.field);

          if (cellOverride === true) {
            return true;
          }
          if (cellOverride === false) {
            return false;
          }

          return column?.disabled !== true;
        }}
        processRowUpdate={processRowUpdate}
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooter
        columnHeaderHeight={columnHeaderHeight}
        rowHeight={rowHeight}
        style={{ height: gridHeight, width: '100%' }}
      />
    </DataTableRoot>
  );
}

export default DataTable;
